import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { trackEvent, trackConversion } from '../components/GoogleAnalytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser } = useContext(AuthContext);
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) {
      navigate('/subscription');
      return;
    }

    checkPaymentStatus(sessionId);
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= maxAttempts) {
      setStatus('timeout');
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    const token = localStorage.getItem('session_token');

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful! Welcome to Premium!');
        
        // Track subscription conversion
        trackConversion('subscription_started', {
          event_category: 'Subscription',
          event_label: 'Premium Plan',
          currency: 'EUR'
        });
        trackEvent('purchase', 'Subscription', 'Premium Plan');
        
        // Refresh user data
        const userResponse = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(userResponse.data);
        
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        toast.error('Payment session expired.');
        return;
      }

      // Continue polling
      setAttempts(attemptCount + 1);
      setTimeout(() => checkPaymentStatus(sessionId, attemptCount + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      toast.error('Error verifying payment. Please contact support.');
    }
  };

  return (
    <Layout>
      <div data-testid="subscription-success-page" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="glass-dark border-white/10 max-w-md w-full">
          <CardContent className="pt-12 pb-8 text-center">
            {status === 'checking' && (
              <>
                <Loader2 className="w-16 h-16 text-blue-400 mx-auto mb-6 animate-spin" />
                <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment</h2>
                <p className="text-gray-400">Please wait while we confirm your payment...</p>
              </>
            )}

            {status === 'success' && (
              <>
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
                <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
                <p className="text-gray-400 mb-6">Welcome to Premium! You now have unlimited access to all features.</p>
                <Button
                  data-testid="go-to-dashboard-button"
                  onClick={() => navigate('/dashboard')}
                  className="bg-blue-600 hover:bg-blue-700 text-white btn-hover"
                >
                  Go to Dashboard
                </Button>
              </>
            )}

            {(status === 'error' || status === 'expired' || status === 'timeout') && (
              <>
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-red-400 text-3xl">!</span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Payment {status === 'expired' ? 'Expired' : 'Error'}</h2>
                <p className="text-gray-400 mb-6">
                  {status === 'expired'
                    ? 'Your payment session has expired. Please try again.'
                    : 'There was an error verifying your payment. Please contact support if you were charged.'}
                </p>
                <Button
                  onClick={() => navigate('/subscription')}
                  className="bg-blue-600 hover:bg-blue-700 text-white btn-hover"
                >
                  Back to Subscription
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default SubscriptionSuccess;
